import React, { useCallback, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { useSelector } from 'react-redux'
import GrinScanTooltip from 'components/Patients/Timeline/V2/MiniTimeline/GrinScanTooltip'
import TimeBasedGrinScanItem from 'components/common/icons/timelineV2/mini-timeline/TimeBasedGrinScanItem'
import { ScanFeedPageSectionNames } from 'consts/timelineConsts'
import { trackEvent } from 'utils/analyticsUtils'
import useScanOralScore from '../../useScanOralScore'

const useStyles = makeStyles(theme => ({
  scanMarker: {
    position: 'relative',
    display: 'inline-flex',
    lineHeight: 0.8,
    cursor: 'pointer',
    border: '3px solid transparent',
    borderRadius: '100%',
    padding: 0,
    background: 'none',
    '&:focus-visible': {
      outline: '2px solid #3C52EF',
      outlineOffset: 2
    }
  },
  selected: {
    borderColor: '#3C52EF'
  }
}))

/**
 * A scan on the time-based timeline.
 *
 * This variation is always drawn by tracking status, whatever the rest of the timeline is showing,
 * so it has its own marker rather than the themed one. The tooltip is the same one the scan-based
 * timeline uses.
 */
const TimeBasedScanMarker = ({ timelineItem, onSelect = section => {}, isSelected = false, analyticsPayload = {} }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const scanTooltipAnchorEl = useRef()

  const { treatment } = useSelector(state => state.treatmentReducer)
  const totalAlignerNumber = useMemo(() => treatment?.totalApplianceNumber, [treatment])
  const trackStatus = useMemo(() => timelineItem?.payload?.trackStatus, [timelineItem])
  const oralScores = useScanOralScore({ oralScores: timelineItem?.payload?.oralScores })

  const [isScanTooltipOpen, setIsScanTooltipOpen] = useState(false)
  const [scanTooltipOpenTime, setScanTooltipOpenTime] = useState(null)

  const handleMouseEnter = useCallback(() => {
    setIsScanTooltipOpen(true)
    setScanTooltipOpenTime(new Date().getTime())
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsScanTooltipOpen(false)

    // Clicking focuses the button, so a later blur runs this again. Without clearing the timestamp
    // that second run reports the time since the first hover - or, with no hover at all, the time
    // since the epoch.
    if (!scanTooltipOpenTime) {
      return
    }
    setScanTooltipOpenTime(null)

    const totalSecondsWasOpen = (new Date().getTime() - scanTooltipOpenTime) / 1000
    if (totalSecondsWasOpen > 0.2) {
      trackEvent('Mini Timeline - scan hover', {
        ...analyticsPayload,
        scanNumber: timelineItem.payload.scanNumber,
        totalSecondsWasOpen
      })
    }
  }, [scanTooltipOpenTime, timelineItem, analyticsPayload])

  return (
    // A button rather than a div: the marker is the only way to open a scan from this view, so it
    // has to be reachable and operable from the keyboard. Focus shows the tooltip as hover does.
    <button
      type="button"
      aria-label={t('pages.patients.selectedPatient.timelineV2.miniTimeline.scanMarkerLabel', {
        date: moment(timelineItem.eventDate).format('MMM D, YYYY')
      })}
      className={[classes.scanMarker, isSelected ? classes.selected : ''].join(' ')}
      ref={scanTooltipAnchorEl}
      onClick={() => onSelect()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <GrinScanTooltip
        anchorEl={scanTooltipAnchorEl.current}
        isOpen={isScanTooltipOpen}
        date={timelineItem.eventDate}
        scanNumber={timelineItem?.payload?.scanNumber}
        alignerNumber={timelineItem?.payload?.alignerNumber}
        totalAlignerNumber={totalAlignerNumber}
        scans={timelineItem?.payload?.scans}
        scanType={timelineItem?.payload?.scanType}
        urgency={timelineItem?.payload?.urgency}
        trackStatus={trackStatus}
        oralScores={oralScores}
        onTrackReasonsClick={() => onSelect(ScanFeedPageSectionNames.ScanTracking)}
      />
      <TimeBasedGrinScanItem trackStatus={trackStatus} alignerNumber={timelineItem?.payload?.alignerNumber} />
    </button>
  )
}

export default React.memo(TimeBasedScanMarker)
